import { ref, type Ref } from 'vue';
import type { CommonWageListRow, CommonWageTaxRate } from '@/types/commonWage';
import type { IncomeTaxRow } from '@/composables/wage/useCommonWageCalc';
import { commonWageApi } from '@/services/api';
import {
  rowToFormStrings,
  getRowToFormDefaultOptions,
  formStringsToNumbers,
  resolveWorkDaysForDisplay,
  type CommonWageFormStrings,
} from '@/utils/commonWageMapper';
import {
  formatHoursDisplay,
  formatWageNumber,
  clampNontaxCashFieldInput,
  normalizeHoursInputValue,
  parseHours,
  parseWageNumber,
  sanitizeHoursInput,
} from '@/composables/wage/useWageCalculator';
import { CWHD_FORM_INPUT_FIELDS, type CcwdFormInputField } from '@/constants/commonWageFields';
import { preparePayloadForRow } from '@/composables/wage/useCommonWageBulkSave';
import { rowEmpKey } from '@/utils/empMatchKey';

export type WageTableDraftMap = Map<string, Partial<CommonWageFormStrings>>;

const HOUR_FIELDS: CcwdFormInputField[] = [
  'cwhd_work_hours',
  'cwhd_overtime_hours',
  'cwhd_night_hours',
  'cwhd_holiday_hours',
];

function rowValueToInputString(
  field: CcwdFormInputField,
  row: CommonWageListRow,
  defaultWorkDays?: number | null
): string {
  if (field === 'cwhd_work_days') {
    const resolved = resolveWorkDaysForDisplay(row.cwhd_work_days, row.cwhd_work_hours, {
      defaultWorkDays,
    });
    if (resolved == null || Number(resolved) === 0) return '';
    return String(Math.floor(Number(resolved)));
  }
  const val = row[field as keyof CommonWageListRow] as number | null | undefined;
  if (val == null || Number(val) === 0) return '';
  if (HOUR_FIELDS.includes(field)) {
    return formatHoursDisplay(Number(val));
  }
  return formatWageNumber(val);
}

export function useCommonWageTableDraft(options: {
  minHourlyWage: Ref<number | undefined>;
  taxRateInfo: Ref<CommonWageTaxRate | null>;
  incomeTaxTable: Ref<IncomeTaxRow[]>;
  year: Ref<number>;
  month: Ref<number>;
  mCode: Ref<string>;
  bizGubun: Ref<string>;
  /** 미입력 근무일수. null이면 공휴일 조회 전 */
  defaultWorkDays?: Ref<number | null>;
}) {
  const drafts = ref<WageTableDraftMap>(new Map());
  const savingEmpKey = ref<string | null>(null);

  const clearDrafts = () => {
    drafts.value = new Map();
  };

  const getDraftStrings = (row: CommonWageListRow): CommonWageFormStrings => {
    const base = rowToFormStrings(row, options.minHourlyWage.value, {
      ...getRowToFormDefaultOptions(row),
      defaultWorkDays: options.defaultWorkDays?.value ?? null,
    });
    const key = rowEmpKey(row);
    const patch = drafts.value.get(key);
    if (!patch) return base;
    const merged = { ...base };
    for (const field of CWHD_FORM_INPUT_FIELDS) {
      if (patch[field] !== undefined) {
        merged[field] = patch[field] as string;
      }
    }
    return merged;
  };

  const getInputValue = (row: CommonWageListRow, field: CcwdFormInputField): string => {
    const key = rowEmpKey(row);
    const patch = drafts.value.get(key);
    if (patch && patch[field] !== undefined) {
      return patch[field] as string;
    }
    return rowValueToInputString(field, row, options.defaultWorkDays?.value ?? null);
  };

  const setInputValue = (row: CommonWageListRow, field: CcwdFormInputField, value: string) => {
    const key = rowEmpKey(row);
    const next = new Map(drafts.value);
    const patch = { ...(next.get(key) || {}) };
    patch[field] = value;
    next.set(key, patch);
    drafts.value = next;
  };

  const onAmountInput = (row: CommonWageListRow, field: CcwdFormInputField, raw: string) => {
    const digits = raw.replace(/[^\d]/g, '');
    let num = digits ? parseInt(digits, 10) : 0;
    num = clampNontaxCashFieldInput(field, num, { silent: true });
    setInputValue(row, field, num ? formatWageNumber(num) : '');
  };

  const onHoursInput = (row: CommonWageListRow, field: CcwdFormInputField, raw: string): string => {
    if (field === 'cwhd_work_days') {
      let cleaned = raw.replace(/[^\d]/g, '');
      const num = parseInt(cleaned, 10) || 0;
      if (num > 31) cleaned = '31';
      setInputValue(row, field, cleaned);
      return cleaned;
    }

    let cleaned = sanitizeHoursInput(raw);
    const num = parseHours(cleaned);
    if (HOUR_FIELDS.includes(field) && num >= 1000) {
      cleaned = '999.9';
    }
    setInputValue(row, field, cleaned);
    return cleaned;
  };

  const onHoursBlur = (row: CommonWageListRow, field: CcwdFormInputField) => {
    if (field === 'cwhd_work_days') {
      const raw = getInputValue(row, field);
      const num = parseInt(raw.replace(/[^\d]/g, ''), 10) || 0;
      setInputValue(row, field, num > 0 ? String(num) : '');
      return;
    }
    if (!HOUR_FIELDS.includes(field)) return;
    setInputValue(row, field, normalizeHoursInputValue(getInputValue(row, field)));
  };

  const validateHourlyOnBlur = (row: CommonWageListRow): boolean => {
    const str = getInputValue(row, 'cwhd_pay_calc_hourly');
    const hourly = parseWageNumber(str);
    const min = options.minHourlyWage.value ?? 0;
    if (min > 0 && hourly > 0 && hourly < min) {
      alert(`시급은 최저시급(${formatWageNumber(min)}원) 이상이어야 합니다.`);
      setInputValue(row, 'cwhd_pay_calc_hourly', formatWageNumber(min));
      return false;
    }
    return true;
  };

  const mergeDraftIntoRow = (row: CommonWageListRow): CommonWageListRow => {
    const form = getDraftStrings(row);
    const nums = formStringsToNumbers(form);
    return {
      ...row,
      cwhd_pay_calc_hourly: nums.cwhd_pay_calc_hourly || row.cwhd_pay_calc_hourly,
      cwhd_work_days: nums.cwhd_work_days || row.cwhd_work_days,
      cwhd_work_hours: nums.cwhd_work_hours || row.cwhd_work_hours,
      cwhd_overtime_hours: nums.cwhd_overtime_hours ?? row.cwhd_overtime_hours,
      cwhd_night_hours: nums.cwhd_night_hours ?? row.cwhd_night_hours,
      cwhd_holiday_hours: nums.cwhd_holiday_hours ?? row.cwhd_holiday_hours,
      cwhd_dayoff: nums.cwhd_dayoff ?? row.cwhd_dayoff,
      cwhd_senior: nums.cwhd_senior ?? row.cwhd_senior,
      cwhd_sunim: nums.cwhd_sunim ?? row.cwhd_sunim,
      cwhd_bonus: nums.cwhd_bonus ?? row.cwhd_bonus,
      cwhd_bosu: nums.cwhd_bosu ?? row.cwhd_bosu,
      cwhd_bojon: nums.cwhd_bojon ?? row.cwhd_bojon,
      cwhd_other: nums.cwhd_other ?? row.cwhd_other,
      cwhd_dang: nums.cwhd_dang ?? row.cwhd_dang,
      cwhd_lunch: nums.cwhd_lunch ?? row.cwhd_lunch,
      cwhd_car: nums.cwhd_car ?? row.cwhd_car,
      cwhd_child: nums.cwhd_child ?? row.cwhd_child,
    };
  };

  const saveRowOnBlur = async (row: CommonWageListRow): Promise<boolean> => {
    if (!options.mCode.value || !options.taxRateInfo.value) return false;
    const key = rowEmpKey(row);
    if (!drafts.value.has(key)) return true;

    if (!validateHourlyOnBlur(row)) {
      // 최저시급 보정 후 계속 저장
    }

    const merged = mergeDraftIntoRow(row);
    savingEmpKey.value = key;
    try {
      const payload = preparePayloadForRow(merged, {
        mCode: options.mCode.value,
        bizGubun: options.bizGubun.value,
        year: options.year.value,
        month: options.month.value,
        taxRateInfo: options.taxRateInfo.value,
        incomeTaxTable: options.incomeTaxTable.value,
        minHourlyWage: options.minHourlyWage.value,
      });
      const res = await commonWageApi.save(payload);
      if (!res.data?.success) {
        throw new Error(res.data?.message || '저장 실패');
      }
      const next = new Map(drafts.value);
      next.delete(key);
      drafts.value = next;
      return true;
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || '알 수 없는 오류';
      alert(`${row.ce_name} 저장 실패: ${message}`);
      return false;
    } finally {
      savingEmpKey.value = null;
    }
  };

  return {
    drafts,
    savingEmpKey,
    clearDrafts,
    getInputValue,
    setInputValue,
    onAmountInput,
    onHoursInput,
    onHoursBlur,
    getDraftStrings,
    mergeDraftIntoRow,
    validateHourlyOnBlur,
    saveRowOnBlur,
  };
}
